import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import clsx from 'clsx'

import ItemPin from '../CleanCard/common/ItemPin'
import { getItemPinCategory } from '../CleanCard/common/itemPinTypes'
import Attachments from './common/Attachments'
import CornerControls from './common/CornerControls'
import { DetailCardAndPinLayout, DetailCardLayout } from './common/DetailCardAndPinLayout'
import ItemCardImage from './common/ItemCardImage'
import PrintOnlyAddressAndNotes from './common/PrintOnlyAddressAndNotes'
import type { DetailItemCardVariant } from './common/types'
import ExperiencePriceAndCTAs from './PriceAndCTAs/ExperiencePriceAndCTAs'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import useToggle from 'hooks/useToggle'
import { SHORT_TIME_HOUR_FORMAT_AM_PM_UPPERCASE } from 'constants/dateFormats'
import GeoContext from 'contexts/geoContext'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Caption from 'LuxKit/Typography/Caption'
import moment from 'moment'
import styled from 'styled-components'
import HoverOnlyRegenerateItemButton from 'tripPlanner/components/CleanCard/common/HoverOnlyRegenerateItemButton'
import { useExperience } from 'tripPlanner/hooks/api/offer'
import { useTrip } from 'tripPlanner/hooks/api/trip'
import useCanEditTrip from 'tripPlanner/hooks/useCanEditTrip'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import type { ExperienceItem } from 'tripPlanner/types/tripItem'
import { formatShortDateRange, isLeBookmark, maybePlaceId } from 'tripPlanner/utils'
import { buildProductLink } from 'tripPlanner/utils/bookmark/restoreCart'
import { getItemPinBadge } from 'tripPlanner/utils/tripItemPin'
import GroupWithSeparator from '../CleanCard/common/GroupWithSeparator'
import TripItemInlineNoteEdit from '../ItemCard/TripItemInlineNoteEdit'
import ConflictingTimesIcon from '../Itinerary/ConflictingTimesIcon'
import OutsideTripDatesIcon from '../Itinerary/OutsideTripDatesIcon'
import { checkIfTripItemHasOverlappingTimes, useItemOutsideOfTripDates } from '../Itinerary/utils'
import { startPlaceOfTripItem } from '../TripMaps/utils'
import EditTripItem from '../TripModal/TripItems/Edit'
import ContainerBreakpoint from '../util/ContainerBreakpoint'
import ItemCardImageCarousel from './common/ItemCardImageCarousel'
import ItemTitle from './common/ItemTitle'
import CustomSuggestionCTAs from './PriceAndCTAs/CustomSuggestionCTAs'

const StyledContainerBreakpoint = styled(ContainerBreakpoint)`
  height: 100%;
`

interface Props {
  item: ExperienceItem
  variant: DetailItemCardVariant
  tripNameLabel?: React.ReactNode
}

function DetailExperienceCard({ item, variant }: Props) {
  const [inFocus, , setInFocusOn, setInFocusOff] = useToggle(false)
  const [showComment, , setShowCommentOn, setShowCommentOff] = useToggle(!!item.notes)
  const canEdit = useCanEditTrip()
  const openModal = useOpenModal()

  const { data: trip } = useTrip({ tripId: item.tripId })

  const place = startPlaceOfTripItem(item)
  const placeId = place ? maybePlaceId(place) : undefined

  const title = item.name

  const { currentCurrency, currentRegionCode } = useContext(GeoContext)
  const experienceId = item.code
  const { data: experience } = useExperience({
    experienceId,
    currentCurrency,
    currentRegionCode,
  })

  const imgId = item.savedItemData?.imageIds?.[0] ?? experience?.images[0]?.id
  const imgUrl = experience?.images[0]?.url
  const offerLink = isLeBookmark(item) ? buildProductLink(item, { experience }) : undefined

  const itemImages = useMemo(() => {
    if (experience?.images) {
      return experience.images
    }
    if (imgId || imgUrl) {
      return [{ id: imgId, url: imgUrl }]
    }
    return []
  }, [experience?.images, imgId, imgUrl])

  const { itemNumber, itemColor } = useMemo(() => {
    if (place?.type === 'GEO' && place.point) {
      return getItemPinBadge(item, variant, trip)
    }
    return {
      itemNumber: undefined,
      itemColor: undefined,
    }
  }, [item, variant, trip, place])

  const startTime = item.startTime ? moment(item.startTime).format(SHORT_TIME_HOUR_FORMAT_AM_PM_UPPERCASE) : undefined
  const overlappingTimes = useMemo(() => {
    if (trip && item) {
      return checkIfTripItemHasOverlappingTimes(trip, item)
    }
    return {
      isOverlapping: false,
      previousItem: undefined,
      nextItem: undefined,
    }
  }, [trip, item])

  const dateDescription = formatShortDateRange(item)
  const showDate = useItemOutsideOfTripDates(item) || variant !== 'itinerary'
  const onClickAddTripItemDates = useCallback(() => {
    openModal(EditTripItem, { item })
  }, [item, openModal])

  const dateWarningIcon =
    variant !== 'drawer' ?
        <OutsideTripDatesIcon
          item={item}
          changeItemDates={onClickAddTripItemDates}
        /> :
      undefined

  const onRequestAddComment = useCallback(() => {
    setShowCommentOn()
    setInFocusOn()
  }, [setShowCommentOn, setInFocusOn])

  const onNotesChange = useCallback((notes: string | undefined) => {
    if (notes) {
      setShowCommentOn()
    } else {
      setShowCommentOff()
    }
  }, [setShowCommentOn, setShowCommentOff])

  const ImageComponent = useMemo(() => {
    if (itemImages.length > 1) {
      return (<>
        <StyledContainerBreakpoint min={640}>
          <ItemCardImageCarousel images={itemImages} className={clsx({ 'showComment': showComment })} />
        </StyledContainerBreakpoint>
        <StyledContainerBreakpoint max={640}>
          <ItemCardImage
            item={item}
            name={place?.name ?? ''}
            imgId={itemImages[0]?.id}
            imgSrc={itemImages[0]?.url}
            placeId={placeId}
            className={clsx({ 'showComment': showComment })}
          />
        </StyledContainerBreakpoint>
      </>)
    }

    return <ItemCardImage
      item={item}
      name={place?.name ?? ''}
      imgId={itemImages[0]?.id}
      imgSrc={itemImages[0]?.url}
      placeId={placeId}
      className={clsx({ 'showComment': showComment })}
    />
  }, [itemImages, item, place?.name, placeId, showComment])

  return (
    <DetailCardAndPinLayout data-testid="detail-card-experience">
      <DetailCardAndPinLayout.Pin className="pin-anchor">
        <ItemPin
          variant={variant}
          category={getItemPinCategory(item.type)}
          itemNumber={itemNumber}
          dayColor={itemColor}
        />
      </DetailCardAndPinLayout.Pin>
      <DetailCardAndPinLayout.CardContainer>
        <DetailCardLayout.Image>
          <HoverOnlyRegenerateItemButton item={item} />
          {ImageComponent}
        </DetailCardLayout.Image>
        <DetailCardLayout.Content>
          <CornerControls item={item} offerLink={offerLink} onRequestAddComment={onRequestAddComment} />
          <VerticalSpacer gap={12}>
            <Group direction="vertical" gap={4}>
              <ItemTitle>{title}</ItemTitle>
              <GroupWithSeparator direction="horizontal" verticalAlign="center" gap={4} desktopGap={8} wrap="wrap">
                {startTime && <Group direction="horizontal" verticalAlign="center" gap={4}>
                  <BodyTextBlock
                    variant="small"
                    colour="neutral-two"
                  >
                    {startTime}
                  </BodyTextBlock>
                  {overlappingTimes.isOverlapping && (
                    <ConflictingTimesIcon
                      item={item}
                      changeItemDates={onClickAddTripItemDates}
                      previousItem={overlappingTimes.previousItem}
                      nextItem={overlappingTimes.nextItem}
                    />
                  )}
                </Group>}
                {dateDescription && showDate && (
                  <BodyTextBlock
                    variant="small"
                    colour="neutral-two"
                    endIcon={dateWarningIcon}
                  >
                    {dateDescription}
                  </BodyTextBlock>
                )}
              </GroupWithSeparator>
            </Group>
            <Attachments item={item} />
            <ContainerBreakpoint min={640}>
              {item.insiderTip && (
                <Caption variant="medium">
                  <FormattedMessage
                    id="sadke5bSfVLdiXgO0h99"
                    defaultMessage="<bold>Why you’ll love this:</bold> {insiderTip}"
                    description="Heading for experience highlights section followed by insider tip"
                    values={{
                      insiderTip: item.insiderTip,
                      bold: (chunks: React.ReactNode) => <Caption variant="medium" colour="highlight-secondary" weight="bold" as="span">{chunks}</Caption>,
                    }}
                  />
                </Caption>
              )}
            </ContainerBreakpoint>
            {experience && (
              <ExperiencePriceAndCTAs
                item={item}
                experience={experience}
                variant={variant}
              />
            )}
            {item.isSuggested && canEdit && !experience && (
              <CustomSuggestionCTAs item={item} />
            )}
            <PrintOnlyAddressAndNotes item={item} />
          </VerticalSpacer>
        </DetailCardLayout.Content>
        {showComment && (
          <DetailCardLayout.Comment>
            <TripItemInlineNoteEdit
              item={item}
              inputAlwaysVisible
              inFocus={inFocus}
              onFocus={setInFocusOn}
              onBlur={setInFocusOff}
              onNotesChange={onNotesChange}
            />
          </DetailCardLayout.Comment>
        )}
      </DetailCardAndPinLayout.CardContainer>
    </DetailCardAndPinLayout>
  )
}

export default DetailExperienceCard
